import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createNotification } from './notificationsService';
import { Event } from './eventsService';

/**
 * Serviço de alertas automáticos para eventos
 * Monitora eventos e cria notificações baseadas em datas e ações dos convidados
 */

const ALERTS_COLLECTION = 'event_alerts';

/**
 * Verifica se um alerta já foi disparado para evitar duplicatas
 */
const hasAlertBeenTriggered = async (
  userId: string,
  eventId: string,
  alertType: string
): Promise<boolean> => {
  try {
    const alertId = `${userId}_${eventId}_${alertType}`;
    const alertRef = doc(db, ALERTS_COLLECTION, alertId);
    const alertDoc = await getDoc(alertRef);
    return alertDoc.exists() && alertDoc.data()?.triggered === true;
  } catch (error) {
    console.error('Erro ao verificar alerta:', error);
    return false;
  }
};

/**
 * Marca um alerta como disparado
 */
const markAlertAsTriggered = async (
  userId: string,
  eventId: string,
  alertType: string
): Promise<void> => {
  try {
    const alertId = `${userId}_${eventId}_${alertType}`;
    const alertRef = doc(db, ALERTS_COLLECTION, alertId);
    await setDoc(alertRef, {
      userId,
      eventId,
      alertType,
      triggered: true,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao marcar alerta como disparado:', error);
  }
};

/**
 * Remove alertas de um evento específico (quando evento é deletado)
 */
export const cleanupEventAlerts = async (eventId: string): Promise<void> => {
  try {
    const alertsRef = collection(db, ALERTS_COLLECTION);
    const q = query(alertsRef, where('eventId', '==', eventId));
    const snapshot = await getDocs(q);
    
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Erro ao limpar alertas do evento:', error);
  }
};

/**
 * Verifica e envia alertas de 5 dias antes do evento
 */
const checkFiveDaysAlert = async (event: Event & { id: string }): Promise<void> => {
  try {
    const eventDate = new Date(event.date);
    const now = new Date();
    const daysUntil = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil === 5) {
      const alreadyTriggered = await hasAlertBeenTriggered(event.userId, event.id, 'days_5');
      if (alreadyTriggered) return;

      await createNotification({
        userId: event.userId,
        type: 'event_update',
        title: 'Evento se aproxima',
        message: `Faltam 5 dias para "${event.title}". Não esqueça de conferir sua lista de tarefas!`,
        metadata: { eventId: event.id, alertType: 'days_5' },
      });

      await markAlertAsTriggered(event.userId, event.id, 'days_5');
    }
  } catch (error) {
    console.error('Erro ao verificar alerta de 5 dias:', error);
  }
};

/**
 * Verifica e envia alertas de 1 dia antes do evento
 */
const checkOneDayAlert = async (event: Event & { id: string }): Promise<void> => {
  try {
    const eventDate = new Date(event.date);
    const now = new Date();
    const daysUntil = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil === 1) {
      const alreadyTriggered = await hasAlertBeenTriggered(event.userId, event.id, 'days_1');
      if (alreadyTriggered) return;

      await createNotification({
        userId: event.userId,
        type: 'event_update',
        title: 'Amanhã temos evento',
        message: `Amanhã é dia de "${event.title}". Já organizou tudo?`,
        metadata: { eventId: event.id, alertType: 'days_1' },
      });

      await markAlertAsTriggered(event.userId, event.id, 'days_1');
    }
  } catch (error) {
    console.error('Erro ao verificar alerta de 1 dia:', error);
  }
};

/**
 * Verifica e envia alertas no dia do evento
 */
const checkEventDayAlert = async (event: Event & { id: string }): Promise<void> => {
  try {
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (eventDate.getTime() === now.getTime()) {
      const alreadyTriggered = await hasAlertBeenTriggered(event.userId, event.id, 'event_day');
      if (alreadyTriggered) return;

      await createNotification({
        userId: event.userId,
        type: 'event_update',
        title: 'Hoje é dia de evento',
        message: `Chegou o dia de "${event.title}". Não esqueça de verificar se seu lavabo também está pronto para uso.`,
        metadata: { eventId: event.id, alertType: 'event_day' },
      });

      await markAlertAsTriggered(event.userId, event.id, 'event_day');
    }
  } catch (error) {
    console.error('Erro ao verificar alerta do dia do evento:', error);
  }
};

/**
 * Verifica se todos os convidados confirmaram presença
 */
const checkAllGuestsConfirmed = async (event: Event & { id: string }): Promise<void> => {
  try {
    if (!event.guests || event.guests.length === 0) return;

    const alreadyTriggered = await hasAlertBeenTriggered(event.userId, event.id, 'all_confirmed');
    if (alreadyTriggered) return;

    const guestsRef = collection(db, 'guests');
    const guestsSnapshot = await getDocs(guestsRef);
    
    let totalGuests = 0;
    let confirmedGuests = 0;

    guestsSnapshot.forEach((doc) => {
      const guestData = doc.data();
      if (event.guests?.includes(doc.id)) {
        totalGuests++;
        if (guestData.rsvp === 'accepted' || guestData.confirmed === true) {
          confirmedGuests++;
        }
      }
    });

    if (totalGuests > 0 && confirmedGuests === totalGuests) {
      await createNotification({
        userId: event.userId,
        type: 'event_update',
        title: 'Todos os convidados confirmaram',
        message: `Todos os ${totalGuests} convidados confirmaram presença em "${event.title}"!`,
        metadata: { eventId: event.id, alertType: 'all_confirmed', totalGuests },
      });

      await markAlertAsTriggered(event.userId, event.id, 'all_confirmed');
    }
  } catch (error) {
    console.error('Erro ao verificar confirmações de convidados:', error);
  }
};

/**
 * Listener principal que monitora eventos do usuário
 */
export const startEventAlertsListener = (userId: string): (() => void) => {
  if (!userId) {
    console.warn('userId não fornecido para listener de alertas');
    return () => {};
  }

  const eventsRef = collection(db, 'events');
  const q = query(eventsRef, where('userId', '==', userId));

  const unsubscribe = onSnapshot(
    q,
    async (snapshot) => {
      const events: Array<Event & { id: string }> = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Event & { id: string });
      });

      for (const event of events) {
        const eventDate = new Date(event.date);
        const now = new Date();

        if (eventDate > now) {
          await checkFiveDaysAlert(event);
          await checkOneDayAlert(event);
          await checkEventDayAlert(event);
          await checkAllGuestsConfirmed(event);
        }
      }
    },
    (error) => {
      console.error('Erro no listener de alertas de eventos:', error);
    }
  );

  console.log('Listener de alertas de eventos iniciado para userId:', userId);
  return unsubscribe;
};

/**
 * Listener para confirmações de presença (RSVP)
 * Notifica o dono do evento quando um convidado confirma
 */
export const startGuestRSVPListener = (userId: string): (() => void) => {
  if (!userId) {
    console.warn('userId não fornecido para listener de RSVP');
    return () => {};
  }

  const guestsRef = collection(db, 'guests');
  const q = query(guestsRef, where('userId', '==', userId));

  let previousGuestsState: Map<string, any> = new Map();

  const unsubscribe = onSnapshot(
    q,
    async (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'modified') {
          const guestId = change.doc.id;
          const newData = change.doc.data();
          const oldData = previousGuestsState.get(guestId);

          if (oldData && oldData.rsvp !== newData.rsvp && newData.rsvp === 'accepted') {
            const guestName = newData.name || 'Um convidado';
            
            await createNotification({
              userId,
              type: 'rsvp_change',
              title: 'Confirmação de Presença',
              message: `${guestName} confirmou presença!`,
              metadata: { guestId, guestName, rsvp: 'accepted' },
            });
          }
        }

        previousGuestsState.set(change.doc.id, change.doc.data());
      });
    },
    (error) => {
      console.error('Erro no listener de RSVP:', error);
    }
  );

  console.log('Listener de RSVP iniciado para userId:', userId);
  return unsubscribe;
};

/**
 * Inicia todos os listeners de alertas para um usuário
 */
export const startAllEventListeners = (userId: string): (() => void) => {
  const unsubscribeAlerts = startEventAlertsListener(userId);
  const unsubscribeRSVP = startGuestRSVPListener(userId);

  return () => {
    unsubscribeAlerts();
    unsubscribeRSVP();
    console.log('Todos os listeners de eventos foram cancelados');
  };
};
